import SharedDialog from '../../../shared/shared-dialog';
import { ChromeDeviceModel, Page } from '../../../../types';
import * as analytics from '../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import { UserKeys, moveChromeDevices } from '../../../api/users-api';
import OrganizationsAutoComplete from '../../../shared/organizations-autocomplete';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface ChromeDevicesMoveDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDevices: ChromeDeviceModel[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function ChromeDevicesMoveDialog({
  open,
  handleClose,
  selectedDevices,
  setSelectedRows,
  changeLogRoles,
}: ChromeDevicesMoveDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autocompleteValue, setAutoCompleteValue] = useState('');
  const queryClient = useQueryClient();

  const deviceIds = selectedDevices.map((device) => device.deviceId);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      moveChromeDevices(
        autocompleteValue,
        {
          deviceIds: deviceIds,
        },
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          deviceIds.length > 1
            ? 'Devices moved successfully!'
            : 'Device moved successfully!',
      });

      const chromeDeviceQueries = queryClient.getQueriesData<
        Page<ChromeDeviceModel[]>
      >({
        queryKey: [UserKeys.ChromeDevicesPage],
      });

      if (chromeDeviceQueries.length > 0) {
        chromeDeviceQueries.map((chromeDeviceQuery) => {
          const queryKey = chromeDeviceQuery[0];
          const deviceList = chromeDeviceQuery[1];
          if (
            deviceList?.results.some((device) =>
              deviceIds.includes(device.deviceId)
            )
          ) {
            queryClient.setQueryData<Page<ChromeDeviceModel[]>>(
              queryKey,
              (oldData) => {
                if (!oldData) return;

                const newData = {
                  ...oldData,
                  results: oldData.results.map((device) =>
                    deviceIds.includes(device.deviceId)
                      ? {
                          ...device,
                          orgUnitPath: autocompleteValue,
                        }
                      : device
                  ),
                };
                return newData;
              }
            );
          }
        });
      }

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          deviceIds.length > 1
            ? 'There was an issue moving the devices to a different OU. Please refresh and try again.'
            : 'There was an issue moving this device to a different OU. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.devices.device_management.move_org_unit, {
        module: ModuleList.modules.devices.name,
        section: ModuleList.modules.devices.sections.chromeDevices,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CanMoveDevices.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={selectedDevices.length > 1 ? 'Move Org Units' : 'Move Org Unit'}
        dialogContent={
          <Stack gap={2}>
            <Typography>
              {selectedDevices.length > 1 ? (
                <>
                  {'Move '}
                  <strong>{selectedDevices.length} selected devices</strong>
                  {'.'}
                </>
              ) : (
                <>
                  {'Move device number '}
                  <strong>{selectedDevices[0].serialNumber}</strong>
                  {'.'}
                </>
              )}
            </Typography>
            <OrganizationsAutoComplete
              autoCompleteValue={autocompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
            />
          </Stack>
        }
        primaryButton={{
          title: selectedDevices.length > 1 ? 'Move Devices' : 'Move Device',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending || autocompleteValue === '',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
